<template>
  <div class="ui-attachment">
    <div v-if="!isFileUploadDisabled">
      <div class="attachment-section" :class="{ 'attachment-section--simple': isSimpleView }">
        <div v-if="!isSimpleView" class="section-header flex items-center gap-5">
          <Icon :name="icon" size="24" class="section-icon" />
          <span class="section-title">{{ displayTitle }}</span>
        </div>

        <div class="section-content">
          <div class="attachment-container" @dragenter.prevent="onDragEnter" @dragleave.prevent="onDragLeave"
            @dragover.prevent="onDragOver" @drop.prevent="onDrop">
            <div class="attachment-content" :class="{ 'drag-over': isDragging }">
              <!-- 업로드 영역 -->
              <div class="file-upload-container">
                <label :for="uniqueId" class="file-upload-label">
                  <Icon name="mdi:plus-circle-outline" size="24" />
                  <span>{{ displayPlaceholder }}</span>
                </label>
                <input :id="uniqueId" type="file" class="file-upload-input" multiple
                  :accept="acceptAttr"
                  @change="handleFileUpload" />
              </div>

              <!-- 파일이 없을 때 -->
              <div class="file-info" v-if="!hasFiles">
                <div class="file-info-text">
                  <Icon name="mdi:file-outline" size="24" class="file-icon" />
                  <span>
                    {{ t('common.fileAttachment.fileCount', { count: 0, max: maxFileCnt }) }}　　{{
                      t('common.fileAttachment.totalSize', {
                        size: '0 Bytes',
                        max: formatFileSize(maxFileSizeLimit),
                      })
                    }}
                  </span>
                </div>
                <div v-if="!isSimpleView" class="file-formats">
                  <span>{{ t('common.fileAttachment.allowedExtensions') }} {{ allowedExts.join(',') }}</span>
                </div>
              </div>

              <!-- 파일이 있을 때 -->
              <div v-if="hasFiles" class="uploaded-files-list">
                <!-- 뱃지 표시 -->
                <div>
                  <div class="file-info-text mb-2">
                    <Icon name="mdi:file-outline" size="24" class="file-icon" />
                    <span>
                      {{ t('common.fileAttachment.fileCount', { count: props.modelValue.length, max: maxFileCnt }) }}　　{{
                        t('common.fileAttachment.totalSize', {
                          size: formatFileSize(totalSize),
                          max: formatFileSize(maxFileSizeLimit),
                        })
                      }}
                    </span>
                  </div>
                  <div v-if="!isSimpleView" class="file-formats">
                    <span>{{ t('common.fileAttachment.allowedExtensions') }} {{ allowedExts.join(',') }}</span>
                  </div>
                </div>
                <!-- 파일 목록 -->
                <div v-for="(file, index) in props.modelValue" :key="index" class="uploaded-file-item mt-10">
                  <div class="file-info-section">
                    <Icon :name="getFileIcon(file)" size="20" class="file-type-icon" />
                    <span class="file-name" @click="downloadFile(file)">{{ file.orignlFileNm || file.name }}</span>
                    <span class="file-size">({{ formatFileSize(file.fileSize || file.size) }})</span>
                  </div>

                  <!-- 신규 파일 삭제 버튼 -->
                  <button v-if="isEmpty(file.atchFileId)" class="file-delete-btn" @click="removeFile(index, false)">
                    <Icon name="mdi:close" size="16" />
                  </button>

                  <!-- 기존 파일 삭제 체크박스 -->
                  <UiCheckbox v-if="isNotEmpty(file.atchFileId)" :model-value="isFileSnMarkedForDelete(file.fileSn)"
                    @update:model-value="checked => toggleDeleteFileId(file.fileSn, checked)">
                    {{ t('common.fileAttachment.deleteAttachment') }}
                  </UiCheckbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 조회 모드에서 파일이 없을 때 -->
    <div v-if="!hasFiles && isFileUploadDisabled" class="empty-attachment">
      <span>{{ t('common.fileAttachment.noAttachments') }}</span>
    </div>
    <!-- 조회 모드에서 파일이 있을 때 -->
    <div
      v-if="hasFiles && isFileUploadDisabled"
      class="attachment-readonly"
      :class="{ 'attachment-readonly--simple': isSimpleView }"
    >
      <div class="uploaded-files-list">
        <!-- 파일 목록 -->
        <div v-for="(file, index) in props.modelValue" :key="index" class="uploaded-file-item">
          <div class="file-info-section">
            <Icon :name="getFileIcon(file)" size="20" class="file-type-icon" />
            <span class="file-name" @click="downloadFile(file)">{{ file.orignlFileNm || file.name }}</span>
            <span class="file-size">({{ formatFileSize(file.fileSize || file.size) }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAttachmentStore } from '~/composables/attachment/useAttachmentStore.js';
import { useCommon } from '~/composables/useCommon.js';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi.js';
import { useUiToast } from '~/composables/useUiToast';

const route = useRoute();
const { t } = useI18n();
const { warning } = useUiToast();

const LEGACY_DEFAULT_TITLES = ['파일첨부', '첨부파일'];

// 고유 ID 생성 (여러 인스턴스가 있을 때 ID 충돌 방지)
const uniqueId = ref(`file-upload-${Math.random().toString(36).substring(2, 9)}`);
const { isNotEmpty, isEmpty } = useCommon();
const { attachmentConfig, fetchAtchFileDownload } = useAttachmentApi();
const { isDragging, dragCounter, imageUrls, deleteFileIds: deleteFileIdsFromStore } = useAttachmentStore();

const props = defineProps({
  title: { type: String, default: '' },
  modelValue: { type: Array, default: () => [] }, // 파일 배열 (신규 파일 + 기존 파일 모두)
  icon: { type: String, default: 'mdi:paperclip' },
  isFileUploadDisabled: { type: Boolean, default: false }, // 업로드 가능 여부 (true: 조회용, false: 등록/수정용)
  uploadPlaceholder: { type: String, default: null }, // 업로드 안내 문구 (null이면 자동 감지)
  maxFileCnt: { type: Number, default: null }, // 화면별 첨부 개수 제한 (미지정 시 attachmentConfig.maxFileCnt 사용)
  allowFileExts: { type: Array, default: null }, // 화면별 확장자 (미지정 시 attachmentConfig.allowFileExts)
  maxFileSize: { type: Number, default: null }, // 화면별 최대 용량 byte (미지정 시 attachmentConfig.maxFileSize)
  isSimpleView: { type: Boolean, default: false },
  /** true면 삭제 fileSn을 전역 store 대신 scopedDeleteFileIds + emit(테이블 셀 등) */
  useScopedDelete: { type: Boolean, default: false },
  scopedDeleteFileIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'update:scopedDeleteFileIds']);

const activeDeleteFileIds = computed(() => {
  if (props.useScopedDelete) {
    return Array.isArray(props.scopedDeleteFileIds) ? props.scopedDeleteFileIds : [];
  }
  return deleteFileIdsFromStore.value;
});

function isFileSnMarkedForDelete(fileSn) {
  return activeDeleteFileIds.value.some(id => String(id) === String(fileSn));
}


// 모바일 여부 감지
const isMobile = computed(() => {
  return route.path.startsWith('/mobile');
});

const displayTitle = computed(() => {
  if (!props.title || LEGACY_DEFAULT_TITLES.includes(props.title)) {
    return t('common.fileAttachment.title');
  }
  return props.title;
});

// 업로드 안내 문구 (모바일/PC 자동 분기)
const displayPlaceholder = computed(() => {
  // props로 명시적으로 전달된 경우 우선 사용
  if (props.uploadPlaceholder !== null) {
    return props.uploadPlaceholder;
  }
  // 모바일이면 모바일 문구, PC면 PC 문구
  return isMobile.value ? t('common.fileAttachment.uploadPlaceholderMobile') : t('common.fileAttachment.uploadPlaceholder');
});

// 파일 존재 여부
const hasFiles = computed(() => props.modelValue.length > 0);

// 이미지 파일만 필터링
const imageFiles = computed(() => props.modelValue.filter(file => isImageFile(file)));

const hasImages = computed(() => imageFiles.value.length > 0);
const maxFileCnt = computed(() => props.maxFileCnt ?? attachmentConfig.maxFileCnt);
const allowedExts = computed(() => {
  const source = Array.isArray(props.allowFileExts) && props.allowFileExts.length ? props.allowFileExts : attachmentConfig.allowFileExts;
  return source.map(ext => String(ext).toLowerCase().replace(/^\./, ''));
});
const maxFileSizeLimit = computed(() => props.maxFileSize ?? attachmentConfig.maxFileSize);
const acceptAttr = computed(() => allowedExts.value.map(ext => `.${ext}`).join(','));

// 총 파일 크기 (raw bytes — 템플릿에서 formatFileSize로 KB/MB 단위 표시)
const totalSize = computed(() => {
  return props.modelValue.reduce((acc, file) => {
    if (file instanceof File) return acc + file.size;
    if (typeof file.fileSize === 'number') return acc + file.fileSize;
    return acc;
  }, 0);
});

function isImageFile(file) {
  if (file instanceof File) return file.type.startsWith('image/');
  const ext = (file.fileExtsn || '').toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'heic', 'webp'].includes(ext);
}

function getFileIcon(file) {
  const type = file.fileExtsn || file.type || '';
  if (type.startsWith('image/')) return 'mdi:image';
  if (type.startsWith('video/')) return 'mdi:video';
  if (type.includes('pdf')) return 'mdi:file-pdf-box';
  if (type.includes('excel') || type.includes('spreadsheet')) return 'mdi:file-excel';
  if (type.includes('word') || type.includes('document')) return 'mdi:file-word';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'mdi:file-powerpoint';
  if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return 'mdi:archive';
  return 'mdi:file';
}

function formatFileSize(bytes) {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function handleFileUpload(e) {
  const newFiles = Array.from(e.target.files || []);
  processFiles(newFiles);
  e.target.value = '';
}

function onDragOver(e) {
  e.preventDefault();
  isDragging.value = true;
}
function onDragEnter(e) {
  e.preventDefault();
  dragCounter.value++;
  isDragging.value = true;
}
function onDragLeave(e) {
  e.preventDefault();
  dragCounter.value--;
  if (dragCounter.value === 0) isDragging.value = false;
}
function onDrop(e) {
  e.preventDefault();
  isDragging.value = false;
  dragCounter.value = 0;
  const newFiles = Array.from(e.dataTransfer?.files || []);
  processFiles(newFiles);
}

// 신규 파일 추가
function processFiles(newFiles) {
  if (!newFiles.length) return;
  const current = props.modelValue || [];

  const invalidFiles = newFiles.filter(file => {
    const ext = (file.name?.split('.').pop() || '').toLowerCase();
    return !allowedExts.value.includes(ext);
  });
  if (invalidFiles.length > 0) {
    warning(t('common.fileAttachment.invalidFileType'));
    return;
  }

  const filteredNew = newFiles.filter(
    newFile => !current.some(file => (file.name || file.orignlFileNm) === newFile.name && (file.size || file.fileSize) === newFile.size),
  );

  const totalFiles = [...current, ...filteredNew];
  if (totalFiles.length > maxFileCnt.value) {
    alert(t('common.fileAttachment.maxFileCountExceeded', { max: maxFileCnt.value }));
    return;
  }
  const totalSize = totalFiles.reduce((acc, file) => acc + (file.size || file.fileSize || 0), 0);
  if (totalSize > maxFileSizeLimit.value) {
    alert(
      t('common.fileAttachment.maxFileSizeExceeded', {
        max: formatFileSize(maxFileSizeLimit.value),
      }),
    );
    return;
  }

  emit('update:modelValue', [...current, ...filteredNew]);
}

// 파일 삭제
function removeFile(index) {
  const updated = [...props.modelValue];
  const removed = updated.splice(index, 1)[0];
  emit('update:modelValue', updated);
}

// 파일 다운로드
function downloadFile(file) {
  if (file.atchFileId && file.fileSn) {
    fetchAtchFileDownload({
      atchFileId: file.atchFileId,
      fileSn: file.fileSn,
    });
  }
}

// 첨부파일 삭제 토글 클릭 시
function toggleDeleteFileId(fileSn, checked) {
  if (props.useScopedDelete) {
    const cur = activeDeleteFileIds.value;
    if (checked) {
      if (!isFileSnMarkedForDelete(fileSn)) {
        emit('update:scopedDeleteFileIds', [...cur, fileSn]);
      }
    } else {
      emit(
        'update:scopedDeleteFileIds',
        cur.filter(id => String(id) !== String(fileSn)),
      );
    }
    return;
  }
  if (checked) {
    if (!deleteFileIdsFromStore.value.includes(fileSn)) {
      deleteFileIdsFromStore.value.push(fileSn);
    }
  } else {
    deleteFileIdsFromStore.value = deleteFileIdsFromStore.value.filter(id => id !== fileSn);
  }
}

onBeforeUnmount(() => {
  imageUrls.value.forEach(url => URL.revokeObjectURL(url));
  imageUrls.value.clear();
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

// SCSS 변수 직접 정의 (임시 해결책)
$primary-color: #00aaff;
$font-size-xs: 10px;
$font-size-sm: 12px;
$font-size-md: 14px;

.ui-attachment {
  width: 100%;
}

.attachment-section {
  margin-bottom: 16px;
  border: 1px dashed #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.attachment-section--simple {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0;

  .attachment-content {
    padding: 10px 12px;
    background-color: #f4f5f6;
  }

  .file-upload-container {
    margin-bottom: 8px;
  }

  .file-upload-label {
    padding: 10px;
    border-color: #d9dde1;
    background-color: #eef0f2;
  }

  .file-info-text {
    font-size: 12px;
  }
}

.section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
}

.section-title {
  font-weight: 500;
  font-size: $font-size-sm;
}

.section-content {
  padding: 0;
}

.attachment-container {
  width: 100%;
}

.attachment-content {
  padding: 16px;
  background-color: #f9f9f9;
  transition: all 0.3s ease;

  &.drag-over {
    border-color: $primary-color;
    background-color: rgba($primary-color, 0.05);
    box-shadow: 0 0 0 2px rgba($primary-color, 0.1);
  }
}

.file-upload-container {
  margin-bottom: 16px;
  text-align: center;
  border-radius: 6px;
}

.file-upload-label {
  display: inline-flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  gap: 8px;
  padding: 30px;
  border: 1px dashed;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  font-size: $font-size-sm;
  transition: color 0.2s;

  &:hover {
    color: $primary-color;
  }
}

.file-upload-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: $font-size-sm;
  color: #666;
}

.file-icon {
  color: #999;
}

.file-formats {
  font-size: $font-size-xs;
  color: #999;
}

.uploaded-files-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

// 미리보기 관련 스타일
.preview-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: $font-size-xs;
  color: #666;

  &:hover {
    background-color: #f5f5f5;
    color: $primary-color;
  }
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.uploaded-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: $font-size-sm;
}

.attachment-readonly--simple {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  background-color: #f4f5f6;

  .uploaded-files-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .uploaded-file-item {
    width: 100%;
    box-sizing: border-box;
    margin: 0;
  }

  .file-info-section {
    min-width: 0;
    width: 100%;
  }

  .file-name {
    max-width: none;
    flex: 1;
    min-width: 0;
  }
}

.file-info-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.file-type-icon {
  color: #666;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  cursor: pointer;
}

.file-size {
  color: #999;
  font-size: $font-size-xs;
}

.image-preview-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
  margin-top: 16px;
}

.preview-title {
  font-size: $font-size-md;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.image-preview-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;

  &:hover .image-overlay {
    opacity: 1;
  }
}

.preview-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.image-name {
  font-size: $font-size-xs;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-delete-btn {
  align-self: flex-end;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.file-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #666;
  }
}

.empty-attachment {
  color: #6c757d;
  font-style: italic;
}
</style>
